import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { submitFormResponse } from '../services/api';
import { PhoneInputField } from './PhoneInputField';
import { phoneValidationRule } from '../utils/phoneValidation';
import { emailValidationRule } from '../utils/emailValidation';
import { getBindingQueryParamValue } from '../utils/bindingValidation';
import {
  evaluateStepVisibility,
  evaluateFieldLogic,
  generateLoopedSteps,
} from '../utils/formLogic';
import {
  FileMetadata,
  Flow,
  FormResponse,
  FormStep,
  NodeType,
  Question,
  ResponseData,
  ValidationRule,
  resolveRatingOptions,
  getSelectedRatingScore,
} from '@dynamic-flow/types';
import { RatingFieldInput } from '@dynamic-flow/ui';
import MarkdownRenderer from './MarkdownRenderer';
import { FormStepLayout } from './FormStepLayout';
import { PageContent, SuccessFallback } from './PageContent';
import classNames from 'classnames';

interface FormRendererProps {
  flow: Flow;
  isPreview?: boolean; // Added for preview mode
  onSubmit?: (data: any) => void; // Custom submit handler for preview
}

export function FormRenderer({
  flow,
  isPreview = false,
  onSubmit,
}: FormRendererProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formSteps, setFormSteps] = useState<FormStep[]>([]);
  const [navigationHistory, setNavigationHistory] = useState<number[]>([0]); // Track navigation path
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formStartTime] = useState(new Date());

  // Preserve the form submission data to next render
  const formResponses = useRef<Record<string, string>>({});

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setError,
    formState: { errors },
  } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });

  const watchedValues = watch();

  // Process flow nodes into form steps
  useEffect(() => {
    if (!flow.nodes || flow.nodes.length === 0) {
      return;
    }

    // Separate landing_page and regular flow_step nodes
    // Note: end_page is NOT included in form steps - it's only shown after submission
    const landingPageNode = flow.nodes.find(
      (node) => node.type === 'landing_page'
    );
    const flowStepNodes = flow.nodes.filter(
      (node) => node.type === 'flow_step'
    );

    let dynamicSteps: FormStep[] = [];

    // Add landing page at the start if it exists
    if (landingPageNode) {
      dynamicSteps.push({
        id: landingPageNode.id,
        node: landingPageNode,
        isVisible: true,
      });
    }

    // Generate steps including looped instances
    flowStepNodes.forEach((node) => {
      if (node.data.loop?.enabled && node.data.loop.sourceFieldId) {
        // Generate looped steps
        const loopedSteps = generateLoopedSteps(node, watchedValues);
        loopedSteps.forEach((loopedNode, index) => {
          dynamicSteps.push({
            id: loopedNode.id,
            node: loopedNode,
            loopIndex: index,
            isVisible: true,
          });
        });
      } else {
        // Regular step
        dynamicSteps.push({
          id: node.id,
          node,
          isVisible: true,
        });
      }
    });

    // Note: end_page is NOT added to form steps - it's only shown after submission

    // Apply step visibility logic (skip for landing_page)
    const updatedSteps = dynamicSteps.map((step, index) => {
      // Landing page is always visible
      if (step.node.type === 'landing_page') {
        return { ...step, isVisible: true };
      }

      if (index === 0) return { ...step, isVisible: true };

      const isVisible = evaluateStepVisibility(
        step.node.id,
        flow,
        watchedValues
      );

      return { ...step, isVisible };
    });

    // Only update if there's an actual change
    const hasChanges =
      JSON.stringify(updatedSteps) !== JSON.stringify(formSteps);

    if (hasChanges && updatedSteps.length > 0) {
      setFormSteps(updatedSteps);
    }
  }, [watchedValues, flow.nodes]);

  // Get visible steps
  const visibleSteps = formSteps.filter((step) => step.isVisible);
  const currentStep = visibleSteps[currentStepIndex];

  // Filter out landing_page for progress calculation
  const regularSteps = visibleSteps.filter(
    (step) => step.node.type !== 'landing_page'
  );
  // Find current step position within regular steps only
  const currentRegularStepIndex = regularSteps.findIndex(
    (step) => step.node.id === currentStep?.node.id
  );
  const currentRegularStepNumber =
    currentRegularStepIndex >= 0 ? currentRegularStepIndex + 1 : 0;

  // Handle navigation
  const handleNext = async () => {
    if (!currentStep) return;

    // Skip validation for landing_page only
    if (currentStep.node.type === 'landing_page') {
      // For landing page, just go to next step
      if (currentStepIndex < visibleSteps.length - 1) {
        const nextStepIndex = currentStepIndex + 1;
        setCurrentStepIndex(nextStepIndex);
        setNavigationHistory((prev) => [...prev, nextStepIndex]);
      }
      return;
    }

    // Get field names for current step for validation
    const fieldNames = currentStep.node.data.questions?.map((q) => q.id) || [];

    // Trigger validation for current step
    let isValid = await trigger(fieldNames);

    // Manual check: single_choice with allowOther must have _value_option filled
    const questions = currentStep.node.data.questions ?? [];
    const formValues = getValues();
    let firstInvalidFieldId: string | null = null;
    for (const q of questions) {
      if (q.type === 'single_choice' && q.options?.length) {
        const selectedValue = formValues[q.id];
        const selectedOpt = q.options.find(
          (o: { value?: string; allowOther?: boolean }) =>
            o.value === selectedValue
        );
        if (selectedOpt?.allowOther) {
          const otherVal = formValues[`${q.id}_value_option`];
          if (
            otherVal == null ||
            (typeof otherVal === 'string' && otherVal.trim() === '')
          ) {
            setError(q.id, { type: 'manual', message: 'กรุณาระบุ' });
            isValid = false;
            if (!firstInvalidFieldId) firstInvalidFieldId = q.id;
          }
        }
      }
    }

    if (!isValid) {
      // Scroll to first error (include field we just set error on)
      const firstError =
        firstInvalidFieldId ??
        Object.keys(errors).find((key) => fieldNames.includes(key));
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check for logic-based navigation
    if (currentStep.node.data.logic) {
      for (const rule of currentStep.node.data.logic) {
        const conditionsMet = rule.conditions.every((condition) => {
          const fieldValue = watchedValues[condition.fieldId];

          switch (condition.operator) {
            case 'equals':
              return fieldValue === condition.value;
            case 'not_equals':
              return fieldValue !== condition.value;
            case 'contains':
              return (
                fieldValue && fieldValue.toString().includes(condition.value)
              );
            case 'greater_than':
              return fieldValue && Number(fieldValue) > Number(condition.value);
            case 'less_than':
              return fieldValue && Number(fieldValue) < Number(condition.value);
            default:
              return true;
          }
        });

        if (conditionsMet && rule.actions?.jumpToStep) {
          // Find target step in all steps first
          const targetStep = formSteps.find(
            (step) => step.node.id === rule.actions.jumpToStep
          );

          if (targetStep && targetStep.isVisible) {
            // Find the index in visible steps
            const targetStepIndex = visibleSteps.findIndex(
              (step) => step.node.id === rule.actions.jumpToStep
            );
            if (targetStepIndex !== -1) {
              setCurrentStepIndex(targetStepIndex);
              // Add to navigation history for proper back navigation
              setNavigationHistory((prev) => [...prev, targetStepIndex]);
              return;
            }
          } else {
            // If target step is not visible, continue to next visible step
            console.warn(
              `Jump target step "${rule.actions.jumpToStep}" is not visible, continuing to next step`
            );
          }
        }
      }
    }

    // Normal navigation to next step
    if (currentStepIndex < visibleSteps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);
      // Add to navigation history for proper back navigation
      setNavigationHistory((prev) => [...prev, nextStepIndex]);
    } else {
      // Last step - submit form
      handleSubmit(onFormSubmit)();
    }
  };

  const handlePrev = () => {
    if (navigationHistory.length > 1) {
      // Remove current step from history and go to previous step in history
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current step
      const previousStepIndex = newHistory[newHistory.length - 1];

      setNavigationHistory(newHistory);
      setCurrentStepIndex(previousStepIndex);
    }
  };

  // Form submission
  const onFormSubmit = async (data: any) => {
    if (isPreview && onSubmit) {
      // Preview mode - use custom handler
      onSubmit(data);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get binding fields data from query parameters
      const bindingFieldsData: any = {};

      // Process all nodes to find fields with binding
      flow.nodes.forEach((node) => {
        if (node.data.questions) {
          node.data.questions.forEach((question) => {
            if (question.binding) {
              const queryParamValue = getBindingQueryParamValue(
                new URLSearchParams(window.location.search),
                question.binding.name
              );

              if (queryParamValue !== null) {
                // Query parameter exists, use its value
                bindingFieldsData[question.id] = queryParamValue;
              } else if (question.binding.fallback) {
                // Use fallback value if query parameter is missing
                bindingFieldsData[question.id] = question.binding.fallback;
              } else if (question.binding.required) {
                // Required binding field is missing, show error
                throw new Error(
                  `Required binding field '${question.binding.name}' is missing from query parameters`
                );
              }
              // If not required and no fallback, skip this field
            }
          });
        }
      });

      // Merge binding fields data with form data
      const completeFormData = { ...data, ...bindingFieldsData };

      const allFiles: FileMetadata[] = [];
      const responses: any[] = [];
      // Convert form data to response format
      Object.entries(completeFormData).forEach(([questionId, value]) => {
        // ข้าม _value_option — จะรวมเข้า response ของ main question แทน
        if (questionId.endsWith('_value_option')) return;

        // Resolve question def: strip `_loop_N`, then exact id.
        // Legacy fallback: `field_<id>` two-segment ids (field_123).
        // Do NOT use first-two-segments only — breaks ids like `field_qa_first_name`.
        const questionBaseId = questionId.replace(/_loop_\d+$/i, '');
        const questionParts = questionId.split('_');
        const legacyTwoSegmentId =
          questionParts.length >= 2
            ? [questionParts[0], questionParts[1]].join('_')
            : questionId;

        let questionType: NodeType = 'text_input';
        let nodeId = '';
        let label = '';
        let fieldKey = '';
        let consentConfig: any = undefined;

        for (const node of flow.nodes) {
          if (node.data.questions) {
            const question = node.data.questions.find(
              (q) =>
                q.id === questionId ||
                q.id === questionBaseId ||
                q.id === legacyTwoSegmentId
            );

            if (question) {
              nodeId = node.id;
              questionType = question.type;
              label = question.label || '';
              fieldKey = question.fieldKey || '';
              consentConfig = question.consentConfig;
              break;
            }
          } else if (
            node.id === questionId ||
            node.id === questionBaseId
          ) {
            // Case 2: Single field nodes (node ID matches question ID)
            nodeId = node.id;
            questionType = node.type;
            label = node.data?.label || '';
            fieldKey = (node.data as { fieldKey?: string })?.fieldKey || '';
            break;
          }
        }

        // API rejects empty label/fieldKey strings — never send blanks
        if (!label) label = questionBaseId;
        if (!fieldKey) fieldKey = questionBaseId;

        // Log warning if nodeId is still empty
        if (!nodeId) {
          console.warn(`Could not find nodeId for questionId: ${questionId}`);

          const fallbackNode = flow.nodes.find(
            (node) => node.id === questionId
          );
          if (fallbackNode) {
            nodeId = fallbackNode.id;
          }
        }

        if (!nodeId) {
          const stepNode = flow.nodes.find((node) => node.type === 'flow_step');
          if (stepNode) {
            nodeId = stepNode.id;
          }
        }

        // Handle File objects separately
        if (value instanceof File) {
          allFiles.push({
            file: value,
            fieldName: label || questionId,
            questionId: questionId,
            id: `${questionId}-${Date.now()}`,
          });

          // Add response metadata without the file
          responses.push({
            nodeId,
            questionId,
            label,
            type: questionType,
            value: null, // Will be replaced with URL after upload
            fileMetadata: {
              fileName: value.name,
              fileSize: value.size,
              fileType: value.type,
            },
            fieldKey,
          });
        }
        // Handle FileList (multiple files)
        else if (value instanceof FileList) {
          Array.from(value).forEach((file, index) => {
            allFiles.push({
              file,
              fieldName: label || questionId,
              questionId: `${questionId}_${index}`,
              id: `${questionId}-${index}-${Date.now()}`,
            });
          });

          responses.push({
            nodeId,
            questionId,
            label,
            type: questionType,
            value: null,
            fileMetadata: {
              fileCount: value.length,
              files: Array.from(value).map((f) => ({
                fileName: f.name,
                fileSize: f.size,
                fileType: f.type,
              })),
            },
            fieldKey,
          });
        }
        // Handle array of Files (from multi-step form)
        else if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File
        ) {
          value.forEach((file, index) => {
            allFiles.push({
              file,
              fieldName: label || questionId,
              questionId: `${questionId}_${index}`,
              id: `${questionId}-${index}-${Date.now()}`,
            });
          });

          responses.push({
            nodeId,
            questionId,
            label,
            type: questionType,
            value: null,
            fileMetadata: {
              fileCount: value.length,
              files: value.map((f) => ({
                fileName: f.name,
                fileSize: f.size,
                fileType: f.type,
              })),
            },
            fieldKey,
          });
        }
        // Regular text/number/etc values
        else {
          const valueOptionRaw = completeFormData[`${questionId}_value_option`];
          const valueOption =
            valueOptionRaw != null && typeof valueOptionRaw === 'string'
              ? valueOptionRaw.trim()
              : valueOptionRaw;
          const hasValueOption =
            questionType === 'single_choice' &&
            valueOption != null &&
            valueOption !== '';

          if (hasValueOption) {
            // รูปแบบตรงตามที่ต้องการ: type, label, value, value_option, nodeId, questionId
            responses.push({
              type: 'text_input',
              label,
              value: typeof value === 'string' ? value.trim() : value,
              value_option: valueOption,
              nodeId,
              questionId,
            });
          } else {
            responses.push({
              nodeId,
              questionId,
              value: typeof value === 'string' ? value.trim() : value,
              label,
              type: questionType,
              fieldKey,
              ...(questionType === 'consent'
                ? { consentConfig: consentConfig }
                : {}),
            });
          }
        }
      });

      const validResponses = responses.filter(
        (response) => response.nodeId
      ) as ResponseData[];

      formResponses.current = validResponses.reduce<Record<string, string>>(
        (acc, resp) => {
          acc[resp.fieldKey] = typeof resp.value === 'string' ? resp.value : '';
          return acc;
        },
        {}
      );

      // Check if we lost any responses due to missing nodeIds
      const originalCount = Object.keys(data).length;
      const validCount = validResponses.length;

      if (validCount < originalCount) {
        const missingCount = originalCount - validCount;
        console.error(
          `Warning: ${missingCount} form field(s) could not be mapped to nodeIds and will be excluded from submission`
        );
      }

      // Debug: Log the responses being sent

      // Calculate completion time in seconds
      const completionTime = Math.round(
        (new Date().getTime() - formStartTime.getTime()) / 1000
      );

      // Detect device type based on user agent
      const userAgent = navigator.userAgent;
      let deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
      if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
        deviceType = 'tablet';
      } else if (
        /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
          userAgent
        )
      ) {
        deviceType = 'mobile';
      }

      const responseData: FormResponse = {
        flowId: flow.id,
        responses: validResponses,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          completionTime,
          deviceType,
          startedAt: formStartTime.toISOString(),
          submittedAt: new Date().toISOString(),
        },
      };

      await submitFormResponse(responseData, allFiles);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission failed:', error);
      setSubmitError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key behavior
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (currentStepIndex === visibleSteps.length - 1) {
        // Last step - submit
        handleSubmit(onFormSubmit)();
      } else {
        // Navigate to next step
        handleNext();
      }
    }
  };

  // Render individual question
  const renderQuestion = (question: Question) => {
    const {
      id,
      type,
      label,
      description,
      required,
      options,
      validation,
      binding,
    } = question;

    // Evaluate field logic using shared utility
    const fieldLogic = evaluateFieldLogic(id, flow, watchedValues);
    const show = fieldLogic.show;
    const isFieldRequired = required || fieldLogic.required;

    // Don't render if logic says to hide
    if (!show) {
      return null;
    }

    // Don't render fields with binding - they will be handled automatically
    if (binding) {
      return null;
    }

    // Get validation rules
    const validationRules = getValidationRules(validation || [], type);

    const fieldProps = {
      name: id,
      control,
      rules: {
        validate: (value: unknown, formValues?: Record<string, unknown>) => {
          if (!isFieldRequired) return undefined;

          // Normalize: handle nullish values early
          if (value == null) return `${label} is required`;

          // Handle arrays
          if (Array.isArray(value)) {
            return value.length === 0 ? `${label} is required` : undefined;
          }

          // Handle strings
          if (typeof value === 'string') {
            if (value.trim().length === 0) return `${label} is required`;
            // single_choice: ถ้าเลือกตัวเลือกที่มี allowOther ต้องกรอกช่อง "อื่นๆ"
            if (type === 'single_choice' && options?.length) {
              const selectedOpt = options.find(
                (o: { value?: string; allowOther?: boolean }) =>
                  o.value === value
              );
              if (selectedOpt?.allowOther) {
                const values = formValues ?? getValues();
                const otherVal = values[`${id}_value_option`];
                if (
                  otherVal == null ||
                  (typeof otherVal === 'string' && otherVal.trim() === '')
                ) {
                  return 'กรุณาระบุ';
                }
              }
            }
            return undefined;
          }

          if (value instanceof File) {
            return undefined;
          }

          if (value instanceof FileList) {
            return value.length === 0 ? `${label} is required` : undefined;
          }

          // Handle objects, numbers, booleans, etc.
          const isEmptyObject =
            typeof value === 'object' && Object.keys(value).length === 0;
          const isFalsy = !value;
          return isEmptyObject || isFalsy ? `${label} is required` : undefined;
        },

        ...validationRules,
        // Add phone validation for phone_input type
        ...(type === 'phone_input' && phoneValidationRule(isFieldRequired)),
        // Add email validation for email_input type
        ...(type === 'email_input' && emailValidationRule(isFieldRequired)),
      },
    };

    const fieldInputClassName = (invalid: boolean) =>
      classNames('form-field-input', invalid && 'is-invalid');

    const choiceOptionClassName = (
      invalid: boolean,
      extra?: Record<string, boolean>
    ) => classNames('choice-option', extra, invalid && 'is-invalid');

    // Render different input types
    return (
      <div className="form-field" key={id}>
        {type !== 'consent' && (
          <label className="flex gap-1">
            <MarkdownRenderer
              source={label}
              className="[&_*]:text-text-primary"
            />
            {isFieldRequired && (
              <span className="form-required-mark ml-1">*</span>
            )}
          </label>
        )}
        {description && <p className="text-text-primary">{description}</p>}
        <Controller
          {...fieldProps}
          defaultValue=""
          render={({ field, fieldState }) => {
            switch (type) {
              case 'text_input':
                return (
                  <div>
                    <input
                      {...field}
                      type="text"
                      placeholder={question.placeholder}
                      onKeyDown={handleInputKeyDown}
                      className={fieldInputClassName(fieldState.invalid)}
                      maxLength={
                        question.validation?.find(
                          (rule) => rule.type === 'max_length'
                        )?.value
                      }
                      minLength={
                        question.validation?.find(
                          (rule) => rule.type === 'min_length'
                        )?.value
                      }
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'email_input':
                return (
                  <div>
                    <input
                      {...field}
                      type="email"
                      placeholder={question.placeholder}
                      onKeyDown={handleInputKeyDown}
                      className={fieldInputClassName(fieldState.invalid)}
                      maxLength={
                        question.validation?.find(
                          (rule) => rule.type === 'max_length'
                        )?.value
                      }
                      minLength={
                        question.validation?.find(
                          (rule) => rule.type === 'min_length'
                        )?.value
                      }
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'number_input':
                return (
                  <div>
                    <input
                      {...field}
                      type="number"
                      placeholder={question.placeholder}
                      onKeyDown={handleInputKeyDown}
                      className={fieldInputClassName(fieldState.invalid)}
                      onWheel={(e) => e.currentTarget.blur()}
                      max={
                        question.validation?.find((rule) => rule.type === 'max')
                          ?.value
                      }
                      min={
                        question.validation?.find((rule) => rule.type === 'min')
                          ?.value
                      }
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'phone_input':
                return (
                  <div>
                    <PhoneInputField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={question.placeholder || 'Enter phone number'}
                      required={required}
                      hasError={fieldState.invalid}
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'single_choice':
                return (
                  <div>
                    <div className="space-y-2">
                      {options?.map((option, index) => (
                        <div key={index}>
                          <label
                            className={classNames(
                              choiceOptionClassName(fieldState.invalid, {
                                selected: field.value === option.value,
                              }),
                              'flex items-center gap-2'
                            )}
                          >
                            <input
                              type="radio"
                              name={id}
                              value={option.value}
                              checked={field.value === option.value}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                            {option.label}
                          </label>
                          {/* แสดง input เมื่อเลือกตัวเลือกที่มี allowOther */}
                          {option.allowOther &&
                            field.value === option.value && (
                              <div className="w-full mb-4">
                                <div className="flex items-center gap-1 w-full">
                                  <span className="form-required-mark shrink-0">
                                    *
                                  </span>
                                  <Controller
                                    name={`${id}_value_option`}
                                    control={control}
                                    defaultValue=""
                                    render={({ field: otherField }) => (
                                      <input
                                        {...otherField}
                                        type="text"
                                        placeholder={
                                          option?.otherPlaceholder ||
                                          'อื่นๆ โปรดระบุ'
                                        }
                                        onKeyDown={handleInputKeyDown}
                                        onChange={(e) => {
                                          otherField.onChange(e);
                                          trigger(id);
                                        }}
                                        className={classNames(
                                          fieldInputClassName(
                                            !!fieldState.error
                                          ),
                                          'w-full min-w-0'
                                        )}
                                      />
                                    )}
                                  />
                                </div>
                                {fieldState.error && (
                                  <p className="form-error-message">
                                    {fieldState.error.message}
                                  </p>
                                )}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'multiple_choice':
                return (
                  <div>
                    <div className="space-y-2">
                      {options?.map((option, index) => {
                        const isSelected = Array.isArray(field.value)
                          ? field.value.includes(option.value)
                          : false;
                        return (
                          <label
                            key={index}
                            className={choiceOptionClassName(
                              fieldState.invalid,
                              {
                                selected: isSelected,
                              }
                            )}
                          >
                            <input
                              type="checkbox"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => {
                                const currentValues = Array.isArray(field.value)
                                  ? field.value
                                  : [];
                                if (e.target.checked) {
                                  field.onChange([
                                    ...currentValues,
                                    option.value,
                                  ]);
                                } else {
                                  field.onChange(
                                    currentValues.filter(
                                      (v) => v !== option.value
                                    )
                                  );
                                }
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'date_picker':
                return (
                  <div>
                    <input
                      {...field}
                      type="date"
                      onKeyDown={handleInputKeyDown}
                      className={fieldInputClassName(fieldState.invalid)}
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'file_upload':
                return (
                  <div>
                    <input
                      type="file"
                      id={id}
                      name={id}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file);
                      }}
                      className={fieldInputClassName(fieldState.invalid)}
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'textarea':
                return (
                  <div>
                    <textarea
                      {...field}
                      placeholder={question.placeholder}
                      rows={4}
                      className={fieldInputClassName(fieldState.invalid)}
                      maxLength={
                        question.validation?.find(
                          (rule) => rule.type === 'max_length'
                        )?.value
                      }
                      minLength={
                        question.validation?.find(
                          (rule) => rule.type === 'min_length'
                        )?.value
                      }
                    />
                    {fieldState.error && (
                      <p className="form-error-message">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );

              case 'rating':
                return (
                  <RatingFieldInput
                    options={resolveRatingOptions(
                      question.options,
                      question.validation
                    )}
                    value={field.value as string | number | undefined}
                    onChange={field.onChange}
                    getSelectedScore={getSelectedRatingScore}
                    error={fieldState.error?.message}
                  />
                );

              case 'consent':
                return (
                  <div>
                    <label htmlFor="consent">
                      <div className="flex gap-2 items-start">
                        <input
                          type="checkbox"
                          id="consent"
                          className="peer sr-only w-5 h-5"
                          onChange={(e) => {
                            // True is only valid value for consent
                            field.onChange(e.target.checked || undefined);
                          }}
                        />
                        <div
                          className={classNames(
                            'form-consent-checkbox w-4 h-4 rounded flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary transition-colors duration-200 shrink-0 mt-1',
                            fieldState.invalid && 'is-invalid'
                          )}
                        >
                          <svg
                            className="w-3 h-3 text-white block"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <MarkdownRenderer
                          source={label}
                          className="select-none text-text-primary [&_*]:text-text-primary"
                        />
                      </div>
                    </label>
                  </div>
                );

              default:
                return <div>Unsupported field type: {type}</div>;
            }
          }}
        />
      </div>
    );
  };

  // Validation rules helper
  const getValidationRules = (
    validationRules: ValidationRule[],
    fieldType: string
  ) => {
    const rules: any = {};

    // Ensure validationRules is an array
    if (!Array.isArray(validationRules)) {
      return rules;
    }

    validationRules.forEach((rule) => {
      // Handle validation rules based on field type
      if (fieldType === 'number_input') {
        // For number inputs, convert length rules to min/max
        switch (rule.type) {
          case 'min_length':
            rules.min = {
              value: rule.value,
              message: rule.message || `Minimum value is ${rule.value}`,
            };
            break;
          case 'max_length':
            rules.max = {
              value: rule.value,
              message: rule.message || `Maximum value is ${rule.value}`,
            };
            break;
          case 'min':
            rules.min = {
              value: rule.value,
              message: rule.message,
            };
            break;
          case 'max':
            rules.max = {
              value: rule.value,
              message: rule.message,
            };
            break;
        }
      } else {
        // For text inputs, use length rules
        switch (rule.type) {
          case 'min_length':
            rules.minLength = {
              value: rule.value,
              message: rule.message,
            };
            break;
          case 'max_length':
            rules.maxLength = {
              value: rule.value,
              message: rule.message,
            };
            break;
          case 'email':
            rules.pattern = {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: rule.message,
            };
            break;
          case 'url':
            rules.pattern = {
              value: /^https?:\/\/.+/,
              message: rule.message,
            };
            break;
          case 'regex':
            rules.pattern = {
              value: new RegExp(rule.value),
              message: rule.message,
            };
            break;
        }
      }
    });

    return rules;
  };

  // Show end page after submission (replaces thank you page)
  if (isSubmitted && !isPreview) {
    // Support both end_page and thank_you for backward compatibility
    const endPageNode = flow.nodes.find(
      (node) => node.type === 'end_page' || node.type === 'thank_you'
    );

    // If there's an end_page or thank_you node, show it with its content
    if (endPageNode) {
      const hasContent =
        endPageNode.data.coverImageUrl || endPageNode.data.markdownContent;

      return (
        <FormStepLayout centerContent>
          <PageContent
            label={endPageNode.data.label || 'Thank You!'}
            description={endPageNode.data.description}
            coverImageUrl={endPageNode.data.coverImageUrl}
            markdownContent={endPageNode.data.markdownContent}
            variables={formResponses.current}
            fallback={!hasContent ? <SuccessFallback /> : undefined}
          />
        </FormStepLayout>
      );
    }

    // Fallback to default success message if no end_page
    return (
      <FormStepLayout centerContent>
        <PageContent label="Thank You!" fallback={<SuccessFallback />} />
      </FormStepLayout>
    );
  }

  // Show error if no steps available
  if (visibleSteps.length === 0) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="w-full rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            No Form Steps Available
          </h1>
          <p className="text-gray-600">
            This form doesn't have any available steps to display.
          </p>
        </div>
      </div>
    );
  }

  return (
    <FormStepLayout
      centerContent={currentStep?.node.type === 'landing_page'}
      footer={
        <>
          <div className="flex justify-between pt-6">
            {currentStep?.node.type === 'landing_page' ? (
              <div className="w-full flex justify-end">
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                >
                  Get Started
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={navigationHistory.length <= 1}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {currentStepIndex === visibleSteps.length - 1 ? (
                  <button
                    type="button"
                    onClick={handleSubmit(onFormSubmit)}
                    disabled={isSubmitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    Next
                  </button>
                )}
              </>
            )}
          </div>

          {submitError && (
            <div className="form-submit-error mt-4 p-3 rounded">
              {submitError}
            </div>
          )}
        </>
      }
    >
      {/* Progress indicator - hide on landing page */}
      {currentStep?.node.type !== 'landing_page' && regularSteps.length > 0 && (
        <div className="mb-8">
          <div
            className="flex justify-between text-sm mb-2"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <span>
              Step {currentRegularStepNumber} of {regularSteps.length}
            </span>
            <span>
              {Math.round(
                (currentRegularStepNumber / regularSteps.length) * 100
              )}
              %
            </span>
          </div>
          <div className="progress-bar h-2">
            <div
              className="progress-bar-fill bg-primary h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(currentRegularStepNumber / regularSteps.length) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {currentStep && (
        <>
          {currentStep.node.type === 'landing_page' ? (
            <PageContent
              label={currentStep.node.data.label}
              description={currentStep.node.data.description}
              coverImageUrl={currentStep.node.data.coverImageUrl}
              markdownContent={currentStep.node.data.markdownContent}
            />
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2 text-text-primary [&_*]:text-text-primary">
                <MarkdownRenderer
                  className="text-text-primary [&_*]:text-text-primary"
                  source={currentStep.node.data.label}
                />
              </h1>
              {currentStep.node.data.description && (
                <p className="mb-6 text-text-primary">
                  {currentStep.node.data.description}
                </p>
              )}

              {visibleSteps.map((step, index) => {
                if (['landing_page', 'ending_page'].includes(step.node.type)) {
                  return null;
                }
                const isCurrent = index === currentStepIndex;
                return (
                  <div
                    key={step.id}
                    style={{
                      display: isCurrent ? 'block' : 'none',
                    }}
                    aria-hidden={!isCurrent}
                  >
                    {step.node.data.questions?.map((question: Question) =>
                      renderQuestion(question)
                    )}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </FormStepLayout>
  );
}
